
import Page from '@/Page.vue';
import { Component } from 'vue-property-decorator';
import moment from 'moment-timezone';

// Timezone pro zobrazování - vždy Praha bez ohledu na TZ prohlížeče
const DISPLAY_TIMEZONE = 'Europe/Prague';
import TpmFiltersBar from '@/libs/tpm-analytics/components/TpmFiltersBar.vue';
import MissingCycleTimesAlert from '@/libs/tpm-analytics/components/MissingCycleTimesAlert.vue';
import { getDateRange } from '@/libs/tpm-analytics/core/date-range.utils';
import { resetMissingCycleTimes } from '@/libs/tpm-analytics/core/missing-cycle-times';
import {
    getOeeTrendStats,
    OeeTrendStats,
    getOeeDaily,
    OeeDailyPoint,
    getOeeByWorkplace,
} from '@/libs/tpm-analytics/tpm-analytics-data-service';
import { getGlobalTargets } from '@/libs/tpm-analytics/tpm-analytics-data-service';
import {
    getFilterById,
    unpackSettings,
    getSelectedFilterLocal,
    setSelectedFilterLocal,
} from '@/libs/tpm-analytics/core/saved-filters';
import { saveChartZoom, loadChartZoom, getOptimalWindowSize, clearChartZoom, saveCategoryZoom, loadCategoryZoom } from '@/libs/tpm-analytics/core/chart-zoom-storage';
import { getDayStartMinutes } from '@/libs/tpm-analytics/core/production-day.utils';

@Component({ components: { TpmFiltersBar, MissingCycleTimesAlert } })
export default class TpmAnalyticsTrends extends Page {
    periodPreset:
        | 'previousDay'
        | 'last7d'
        | 'last14d'
        | 'last30d'
        | 'last3m'
        | 'thisWeek'
        | 'lastWeek'
        | 'thisMonth'
        | 'lastMonth'
        | 'thisQuarter'
        | 'lastQuarter'
        | 'thisYear'
        | 'lastYear'
        | 'custom' = 'last7d';
    customFrom: string | null = null;
    customTo: string | null = null;
    selectedLocationIds: string[] = [];
    selectedWorkplaceIds: string[] = [];
    selectedProjectIds: string[] = [];
    selectedShiftTypeIds: string[] = [];
    selectedShiftTeamTypeIds: string[] = [];

    // KPI
    avgOee: number | null = null;
    prevAvgOee: number | null = null;
    volatilityPct: number | null = null;
    improvementToTargetPct: number | null = null;
    improvementToWorldClassPct: number | null = null;
    targetOeeText: string | null = null;
    worldClassText: string | null = null;

    // Trend graph state
    daily: OeeDailyPoint[] = [];
    viewStart: number | null = null;
    viewEnd: number | null = null;
    // Pro kategorickou osu: indexy viditelných kategorií
    categoryStartIndex: number | null = null;
    categoryEndIndex: number | null = null;
    oeeGranularity: 'day' | 'week' | 'month' = 'day';
    filtersDebounceTimer: any = null;

    get windowDays(): number {
        return getOptimalWindowSize(this.oeeGranularity);
    }

    private getTimeUnit(gran: 'day' | 'week' | 'month'): 'day' | 'isoWeek' | 'month' {
        if (gran === 'week') return 'isoWeek';
        if (gran === 'month') return 'month';
        return 'day';
    }

    // Monthly goals & ranking
    monthlyRows: Array<{
        key: string;
        label: string;
        progressPct: number;
        actualText: string;
        ofTargetText: string;
        color: string;
    }> = [];
    workplaceTop: Array<{ workplaceId: string | null; workplaceName: string; oee: number; performance: number }> = [];


    getRange() {
        return getDateRange({
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            granularity: this.oeeGranularity,
        });
    }

    onFiltersChanged() {
        // Debounce 200ms - zabráníme duplicitnímu volání při rychlé změně více filtrů
        if (this.filtersDebounceTimer) clearTimeout(this.filtersDebounceTimer);
        this.filtersDebounceTimer = setTimeout(() => {
            this.executeFiltersChanged();
        }, 200);
    }

    executeFiltersChanged() {
        // Reset missing cycle times na začátku celého načítání
        resetMissingCycleTimes();
        const { from, to } = this.getRange();
        const filters = {
            from,
            to,
            tenantId: '',
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        } as any;
        getOeeTrendStats(filters).then((s: OeeTrendStats) => {
            this.avgOee = s.avgOee;
            this.prevAvgOee = s.prevAvgOee;
            this.volatilityPct = s.volatilityPct;
            this.improvementToTargetPct = s.improvementToTargetPct;
            this.improvementToWorldClassPct = s.improvementToWorldClassPct;
            this.targetOeeText = s.targetOeePctText || null;
            this.worldClassText = s.worldClassPctText || null;
        });
        getOeeDaily({ ...filters, granularity: this.oeeGranularity } as any).then((rows: any) => {
            this.daily = rows;
            this.resetViewport();
            this.loadMonthlyAndRanking(from, to);
        });
        const fn = (this.$root as any).$tpmOnStateChanged;
        if (typeof fn === 'function') fn();
    }

    $watch!: any;
    created() {
        // Register callbacks for parent
        (this.$root as any).$tpmGetCurrentState = this.getCurrentFilterState.bind(this);
        (this.$root as any).$tpmApplyState = this.applyFilterState.bind(this);
        const initState = (this.$root as any).$tpmInitialState;
        const initSid = (this.$root as any).$tpmInitialSid;
        if (initState && Object.keys(initState || {}).length > 0) {
            this.applyFilterState(initState, false);
            return;
        }
        if (initSid) {
            setSelectedFilterLocal(initSid);
            getFilterById(initSid).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
            });
            return;
        }
        const localId = getSelectedFilterLocal();
        if (localId) {
            setSelectedFilterLocal(localId);
            getFilterById(localId).then((model) => {
                const state = unpackSettings(model?.settings);
                this.applyFilterState(state, false);
            });
        }
        // Pokud není žádný initial state, mounted() zavolá load() s defaultními hodnotami
    }

    mounted() {
        // V mounted() VŽDY voláme load() - created() pouze nastavil stav
        this.onFiltersChanged();
        // Watch pro změnu granularity
        this.$watch('oeeGranularity', (newVal: 'day' | 'week' | 'month', oldVal: 'day' | 'week' | 'month') => {
            if (oldVal && newVal !== oldVal) {
                // Při změně granularity resetujeme zoom
                this.resetViewport();
            }
        });
    }

    destroyed() {
        if ((this.$root as any).$tpmGetCurrentState === this.getCurrentFilterState)
            delete (this.$root as any).$tpmGetCurrentState;
        if ((this.$root as any).$tpmApplyState === this.applyFilterState) delete (this.$root as any).$tpmApplyState;
    }

    getCurrentFilterState() {
        return {
            periodPreset: this.periodPreset,
            customFrom: this.customFrom,
            customTo: this.customTo,
            selectedLocationIds: this.selectedLocationIds,
            selectedWorkplaceIds: this.selectedWorkplaceIds,
            selectedProjectIds: this.selectedProjectIds,
            selectedShiftTypeIds: this.selectedShiftTypeIds,
            selectedShiftTeamTypeIds: this.selectedShiftTeamTypeIds,
            granularity: this.oeeGranularity,
        } as any;
    }
    applyFilterState(state: any, reload = true) {
        // reload = true: uživatel změnil filtry → načti data
        // reload = false: inicializace v created() → pouze nastav stav, data se načtou v mounted()
        const periodChanged = state?.periodPreset && state.periodPreset !== this.periodPreset;
        const customDatesChanged = (state?.customFrom && state.customFrom !== this.customFrom) || 
                                   (state?.customTo && state.customTo !== this.customTo);
        
        if (state?.periodPreset) this.periodPreset = state.periodPreset;
        if (state?.customFrom) this.customFrom = state.customFrom;
        if (state?.customTo) this.customTo = state.customTo;
        this.selectedLocationIds = Array.isArray(state?.selectedLocationIds) ? state.selectedLocationIds : [];
        this.selectedWorkplaceIds = Array.isArray(state?.selectedWorkplaceIds) ? state.selectedWorkplaceIds : [];
        this.selectedProjectIds = Array.isArray(state?.selectedProjectIds) ? state.selectedProjectIds : [];
        this.selectedShiftTypeIds = Array.isArray(state?.selectedShiftTypeIds) ? state.selectedShiftTypeIds : [];
        this.selectedShiftTeamTypeIds = Array.isArray(state?.selectedShiftTeamTypeIds) ? state.selectedShiftTeamTypeIds : [];
        if (state?.granularity) {
            this.oeeGranularity = state.granularity;
        } else if (state?.oeeGranularity) {
            // Zpětná kompatibilita se starými uloženými filtry
            this.oeeGranularity = state.oeeGranularity;
        }
        
        // Pokud se změnilo období, vymazat uložený zoom aby se použil nový default (maximum dat)
        if (periodChanged || customDatesChanged) {
            clearChartZoom('trends-oee');
        }
        
        if (reload) {
            this.onFiltersChanged();
        }
    }

    // Agregovaná data pro trend chart (podle granularity)
    get aggregatedDataForChart(): OeeDailyPoint[] {
        if (this.oeeGranularity === 'day') {
            return this.daily;
        }

        const dayStartMinutes = getDayStartMinutes();
        const dayStartMs = dayStartMinutes * 60 * 1000;

        const grouped = new Map<string, { oee: number[]; availability: number[]; performance: number[]; quality: number[] }>();

        this.daily.forEach((d) => {
            let periodKey: number;
            if (this.oeeGranularity === 'week') {
                periodKey = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else {
                periodKey = moment(d.day).startOf('month').valueOf() + dayStartMs;
            }
            const key = moment(periodKey).toISOString();

            const e = grouped.get(key) || { oee: [], availability: [], performance: [], quality: [] };
            if (d.oee != null) e.oee.push(d.oee);
            if (d.availability != null) e.availability.push(d.availability);
            if (d.performance != null) e.performance.push(d.performance);
            if (d.quality != null) e.quality.push(d.quality);
            grouped.set(key, e);
        });

        const aggregated: OeeDailyPoint[] = [];
        grouped.forEach((value, key) => {
            const avg = (arr: number[]) => (arr.length > 0 ? arr.reduce((a, b) => a + b, 0) / arr.length : null);
            aggregated.push({
                day: key,
                oee: avg(value.oee),
                availability: avg(value.availability),
                performance: avg(value.performance),
                quality: avg(value.quality),
            });
        });

        return aggregated.sort((a, b) => new Date(a.day).getTime() - new Date(b.day).getTime());
    }

    // Trend chart helpers
    get trendOptions() {
        const self = this;
        
        // Vygenerujeme kategorie (labely) pro osu X
        const categories = this.aggregatedDataForChart.map((d) => {
            const dayStartMinutes = getDayStartMinutes();
            const dayStartMs = dayStartMinutes * 60 * 1000;
            let timestamp: number;
            
            if (this.oeeGranularity === 'week') {
                timestamp = moment(d.day).startOf('isoWeek').valueOf() + dayStartMs;
            } else if (this.oeeGranularity === 'month') {
                timestamp = moment(d.day).startOf('month').valueOf() + dayStartMs;
            } else {
                timestamp = moment(d.day).startOf('day').valueOf() + dayStartMs;
            }
            
            return self.formatAxisLabel(timestamp, self.oeeGranularity);
        });
        
        return {
            chart: { id: 'trends-oee', toolbar: { show: false }, zoom: { enabled: false }, animations: { enabled: false } },
            stroke: { width: [3, 0, 0, 0], curve: 'smooth' },
            markers: { size: 3 },
            dataLabels: { enabled: false },
            xaxis: {
                type: 'category',
                categories: categories,
                min: this.categoryStartIndex === 0 ? -0.8 : (this.categoryStartIndex != null ? this.categoryStartIndex - 0.3 : undefined),
                max: this.categoryEndIndex != null ? this.categoryEndIndex + 1.3 : undefined,
                labels: {
                    rotate: self.oeeGranularity === 'week' ? -45 : 0,
                    formatter(value: any) {
                        // Pro týdny rozdělíme label na dva řádky pomocí |
                        if (self.oeeGranularity === 'week' && typeof value === 'string' && value.includes('|')) {
                            const parts = value.split('|');
                            return parts;
                        }
                        return value;
                    },
                    style: {
                        cssClass: 'apexcharts-xaxis-label-multiline',
                    },
                },
            },
            grid: {
                padding: {
                    right: 5,
                    left: this.categoryStartIndex === 0 ? 0 : 10,
                }
            },
            yaxis: {
                labels: { formatter: (v: any) => (isFinite(Number(v)) ? Number(v).toFixed(0) + '%' : '') },
                min: 0,
                max: 100,
            },
            fill: {
                type: 'gradient',
                gradient: { shade: 'light', shadeIntensity: 0.06, opacityFrom: 0.9, opacityTo: 0.85, stops: [0, 80, 100] },
            },
            colors: ['#2196F3', '#4CAF50', '#FF9800', '#F44336'], // OEE-modrá, A-zelená, E-oranžová, Q-červená
            legend: { show: true, position: 'top', horizontalAlign: 'center' },
            tooltip: {
                enabled: true,
                shared: true,
                intersect: false,
                y: {
                    formatter: (val: number) => {
                        if (val == null || isNaN(val)) return '-';
                        return self.formatPct(val / 100);
                    }
                }
            },
        } as any;
    }
    get trendSeries() {
        // Pro kategorickou osu používáme jednoduché pole čísel
        const oee = this.aggregatedDataForChart.map((d) => d.oee == null ? null : +(d.oee * 100).toFixed(2));
        const a = this.aggregatedDataForChart.map((d) => d.availability == null ? null : +(d.availability * 100).toFixed(2));
        const e = this.aggregatedDataForChart.map((d) => d.performance == null ? null : +(d.performance * 100).toFixed(2));
        const q = this.aggregatedDataForChart.map((d) => d.quality == null ? null : +(d.quality * 100).toFixed(2));
        
        return [
            { name: 'OEE', data: oee, type: 'line' },
            { name: 'A', data: a, type: 'line' },
            { name: 'E', data: e, type: 'line' },
            { name: 'Q', data: q, type: 'line' },
        ] as any;
    }
    formatDay(ts: number): string {
        return moment(ts).format('D.M.');
    }
    formatAxisLabel(ts: number, gran: 'day' | 'week' | 'month', forTooltip = false): string {
        const m = moment(ts);
        if (!m.isValid()) return '';
        if (gran === 'month') return m.format('MM/YYYY');
        if (gran === 'week') {
            const start = m.clone().startOf('isoWeek');
            const end = m.clone().endOf('isoWeek');
            const week = m.isoWeek();
            const weekLabel = `${week}. týden`;
            const dateRange = `(${start.format('DD.MM.')} - ${end.format('DD.MM.')})`;
            
            // Pro tooltip používáme HTML break
            if (forTooltip) {
                return `${weekLabel}<br/>${dateRange}`;
            }
            // Pro osu X vracíme pole jako string se speciálním separátorem
            return weekLabel + '|' + dateRange;
        }
        return m.format('D.M.');
    }
    resetViewport() {
        const data = this.aggregatedDataForChart;
        if (data.length === 0) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            return;
        }

        // Pro kategorickou osu: zobrazíme všechna data (null = všechno)
        this.categoryStartIndex = null;
        this.categoryEndIndex = null;
    }
    shiftPercent(percent: number) {
        const data = this.aggregatedDataForChart;
        if (data.length === 0) return;

        let startIdx: number;
        let endIdx: number;

        if (this.categoryStartIndex == null || this.categoryEndIndex == null) {
            const windowSize = Math.min(10, data.length);
            if (data.length <= windowSize) return;

            if (percent > 0) {
                startIdx = 0;
                endIdx = windowSize - 1;
            } else {
                startIdx = data.length - windowSize;
                endIdx = data.length - 1;
            }
        } else {
            startIdx = this.categoryStartIndex;
            endIdx = this.categoryEndIndex;
        }

        const visibleCount = endIdx - startIdx + 1;
        const calculatedShift = visibleCount * percent;
        const shift = percent > 0 ? Math.max(1, Math.round(calculatedShift)) : Math.min(-1, Math.round(calculatedShift));

        let newStart = startIdx + shift;
        let newEnd = endIdx + shift;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + visibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = newEnd - visibleCount + 1;
            if (newStart < 0) {
                newStart = 0;
                newEnd = data.length - 1;
            }
        }

        if (newStart === startIdx && newEnd === endIdx) return;

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }
    zoomIn() {
        const data = this.aggregatedDataForChart;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        const minVisible = 2;
        const newVisibleCount = Math.max(minVisible, Math.round(visibleCount * 0.75));
        
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }
    zoomOut() {
        const data = this.aggregatedDataForChart;
        if (data.length === 0) return;

        const startIdx = this.categoryStartIndex ?? 0;
        const endIdx = this.categoryEndIndex ?? data.length - 1;
        const visibleCount = endIdx - startIdx + 1;
        
        const maxVisible = data.length;
        const newVisibleCount = Math.min(maxVisible, Math.round(visibleCount / 0.75));

        if (newVisibleCount >= data.length) {
            this.categoryStartIndex = null;
            this.categoryEndIndex = null;
            return;
        }
        
        const center = Math.round((startIdx + endIdx) / 2);
        let newStart = center - Math.round(newVisibleCount / 2);
        let newEnd = newStart + newVisibleCount - 1;

        if (newStart < 0) {
            newStart = 0;
            newEnd = newStart + newVisibleCount - 1;
        }
        if (newEnd >= data.length) {
            newEnd = data.length - 1;
            newStart = Math.max(0, newEnd - newVisibleCount + 1);
        }

        this.categoryStartIndex = newStart;
        this.categoryEndIndex = newEnd;
    }

    saveCurrentZoom() {
        const data = this.aggregatedDataForChart;
        if (data.length > 0) {
            saveCategoryZoom('trends-oee', {
                granularity: this.oeeGranularity,
                startIndex: this.categoryStartIndex,
                endIndex: this.categoryEndIndex,
            });
        }
    }

    // Formatting helpers
    formatPct(v: number | null): string {
        if (v == null || isNaN(v as any)) return '—';
        return ((v as number) * 100).toFixed(1) + '%';
    }
    formatPctVal(v: number | null): string {
        if (v == null || isNaN(v as any)) return '—';
        return (v as number).toFixed(1) + '%';
    }
    formatDelta(pct: number | null): string {
        if (pct == null || isNaN(pct as any)) return '—';
        const sign = (pct as number) >= 0 ? '+' : '';
        return sign + (pct as number).toFixed(1) + '%';
    }
    get deltaOeePct(): number | null {
        if (this.avgOee == null || this.prevAvgOee == null) return null;
        return +((this.avgOee - this.prevAvgOee) * 100).toFixed(1);
    }
    async loadMonthlyAndRanking(from: string, to: string) {
        // averages from daily
        const avg = (arr: Array<number | null>) => {
            const v = arr.filter((x) => x != null).map((x) => x as number);
            return v.length ? v.reduce((a, b) => a + Math.max(0, Math.min(1, b)), 0) / v.length : 0;
        };
        const oeeAvg = avg(this.daily.map((d) => d.oee));
        const aAvg = avg(this.daily.map((d) => d.availability));
        const eAvg = avg(this.daily.map((d) => d.performance));
        const qAvg = avg(this.daily.map((d) => d.quality));
        const targets = await getGlobalTargets(from, to);
        const makeRow = (key: string, label: string, val: number, target: number | null, color: string) => {
            const progress = target && target > 0 ? Math.min(100, +((val / target) * 100).toFixed(1)) : +(val * 100).toFixed(1);
            const ofTarget =
                target && target > 0 ? `${+((val / target) * 100).toFixed(1)}% ${this.$t('tpmAnalytics.ofTarget')}` : '';
            return { key, label, progressPct: progress, actualText: this.formatPct(val), ofTargetText: ofTarget, color };
        };
        this.monthlyRows = [
            makeRow('oee', 'OEE', oeeAvg, targets.oee, '#2e7d32'),
            makeRow('a', this.$t('tpmAnalytics.availability') as any, aAvg, targets.availability, '#2e7d32'),
            makeRow('e', this.$t('tpmAnalytics.performance') as any, eAvg, targets.performance, '#1976d2'),
            makeRow('q', this.$t('tpmAnalytics.quality') as any, qAvg, targets.quality, '#ef6c00'),
        ];

        const wp = await getOeeByWorkplace({
            from,
            to,
            locationIds: this.selectedLocationIds,
            workplaceIds: this.selectedWorkplaceIds,
            projectIds: this.selectedProjectIds,
            shiftTypeIds: this.selectedShiftTypeIds,
            shiftTeamTypeIds: this.selectedShiftTeamTypeIds,
        } as any);
        const sorted = [...wp].sort((x, y) => (y.oee || 0) - (x.oee || 0)).slice(0, 4);
        this.workplaceTop = sorted.map((x) => ({
            workplaceId: x.workplaceId,
            workplaceName: x.workplaceName,
            oee: Math.max(0, Math.min(1, x.oee || 0)),
            performance: Math.max(0, Math.min(1, x.performance || 0)),
        }));
    }
}
